// Pure rendering for the Terminal's long listing (`ls -l`).
//
// Kept out of the component because column alignment is the entire point of
// `-l`: a listing whose name column wobbles row to row is harder to read than
// the plain listing it replaced. Alignment is decided here, where it can be
// asserted, rather than in JSX where it cannot.
//
// No imports: this runs in the browser bundle and under `node --test`
// unchanged.
//
// KEEP IN SYNC with ./shell-format.d.mts (hand-written type mirror).

const UNITS = ["K", "M", "G", "T"];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Six months either side of now — bash's cutoff for showing a clock time. */
const RECENT_MS = 1000 * 60 * 60 * 24 * 180;

/** Width of every date cell, so the name column never moves. */
const DATE_WIDTH = 12;

/**
 * Bytes as something a human can read at a glance.
 *
 * Junk (missing, negative, non-numeric) renders as `0B` rather than `NaNB`:
 * these values come off the wire, and one absent field must not corrupt the
 * whole listing.
 *
 * @param {unknown} bytes
 * @returns {string}
 */
export function humanSize(bytes) {
  const n = typeof bytes === "number" && Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  if (n < 1024) return `${Math.round(n)}B`;
  let value = n / 1024;
  let unit = UNITS[0];
  for (let i = 1; i < UNITS.length && value >= 1024; i++) {
    value /= 1024;
    unit = UNITS[i];
  }
  // One decimal only while it carries information: "1.5K" is worth the width,
  // "1023.4K" is not.
  return value < 10 ? `${value.toFixed(1)}${unit}` : `${Math.round(value)}${unit}`;
}

/**
 * A fixed-width timestamp: `Jul 25 14:32` for something recent, `Jan  2  2019`
 * for anything older (or dated in the future) — the year matters more than the
 * minute once a file is old, which is the same trade `ls` makes.
 *
 * Always returns exactly {@link DATE_WIDTH} characters, including for an
 * unparseable value, because the caller pads nothing after it.
 *
 * @param {unknown} mtime ISO string from an FsEntry
 * @param {number} [now]
 * @returns {string}
 */
export function shortDate(mtime, now) {
  const ref = typeof now === "number" && Number.isFinite(now) ? now : Date.now();
  const t = typeof mtime === "string" ? new Date(mtime) : new Date(NaN);
  if (Number.isNaN(t.getTime())) return "-".padStart(DATE_WIDTH);
  const day = String(t.getDate()).padStart(2, " ");
  const head = `${MONTHS[t.getMonth()]} ${day}`;
  const tail =
    Math.abs(ref - t.getTime()) < RECENT_MS
      ? `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`
      : ` ${t.getFullYear()}`;
  return `${head} ${tail}`;
}

/**
 * One `ls -l` line per entry, in the order given, with the size column
 * right-aligned so every name starts at the same column.
 *
 * @param {ReadonlyArray<{name: string, type: string, size?: unknown, mtime?: unknown}>} entries
 * @param {number} [now]
 * @returns {string[]}
 */
export function formatLongListing(entries, now) {
  const list = Array.isArray(entries) ? entries : [];
  const rows = list.map((e) => ({
    type: e && e.type === "dir" ? "d" : "-",
    size: humanSize(e ? e.size : 0),
    date: shortDate(e ? e.mtime : undefined, now),
    name: e && e.type === "dir" ? `${e.name}/` : String(e ? e.name : ""),
  }));
  const sizeWidth = Math.max(0, ...rows.map((r) => r.size.length));
  return rows.map(
    (r) => `${r.type}  ${r.size.padStart(sizeWidth)}  ${r.date}  ${r.name}`
  );
}
